import type { CurrentUser } from '@/api/types';

export interface ViewDef {
  path: string;
  permission?: string;
  /**
   * Alternates that satisfy the gate on their own: an OR tuple, mirroring the
   * `permission_map` tuples the backend accepts. The primary `permission` (if
   * set) and every `orPermissions` entry are equally valid.
   */
  orPermissions?: readonly string[];
  /** The user needs at least one of these role codes in addition to permission. */
  anyRoles?: readonly string[];
}

/**
 * Route access, not the menu. Each entry is the guard of a screen that exists
 * in code (`ProtectedRoute view=`, home tiles, in-page tabs). Where a screen
 * appears in the menu, and under which label, is data served by
 * `/api/v1/navigation/me/` — see `features/navigation`.
 */
export const VIEWS = {
  // Single menu entry for the whole module; its tabs (SchedulingNav) gate each page.
  scheduling: {
    path: '/horarios',
    permission: 'scheduling.view_work_schedule',
    orPermissions: ['scheduling.view_work_schedule_assignment'],
  },
  workSchedules: {
    path: '/horarios',
    permission: 'scheduling.view_work_schedule',
  },
  scheduleAssignments: {
    path: '/horarios/asignaciones',
    permission: 'scheduling.view_work_schedule_assignment',
  },
  scheduleCalendar: {
    path: '/horarios/calendario',
    permission: 'scheduling.view_work_schedule_assignment',
  },
  scheduleAvailability: {
    path: '/horarios/disponibilidad',
    permission: 'scheduling.view_work_schedule_assignment',
  },
  home: { path: '/' },
  dashboard: {
    path: '/gerencia',
    permission: 'dashboard.view_gerencia',
  },
  sites: {
    path: '/organizacion/sedes',
    permission: 'organization.view_site',
  },
  departments: {
    path: '/organizacion/areas',
    permission: 'organization.view_department',
  },
  positions: {
    path: '/organizacion/cargos',
    permission: 'organization.view_position',
  },
  quickPicks: {
    path: '/organizacion/sugerencias-rapidas',
    permission: 'organization.view_quick_pick',
  },
  themes: {
    path: '/organizacion/temas',
    permission: 'organization.view_manual',
  },
  versionReview: {
    path: '/organizacion/revisar-versiones',
    permission: 'organization.view_manual',
  },
  campaigns: {
    path: '/evaluacion/campanas',
    permission: 'evaluations.view_campaign',
  },
  newCampaign: { path: '/evaluacion/campanas/nueva', permission: 'evaluations.manage_campaign' },
  campaignTracking: {
    path: '/evaluacion/seguimiento',
    permission: 'evaluations.view_campaign',
  },
  // Reevaluaciones: la evaluación oral que el jefe toma a quien desaprobó. La
  // regla de acceso es solo el permiso (el backend decide el alcance de cada
  // fila): quien lo tiene ve la sección, sin atarla a un rol.
  oralEvaluations: {
    path: '/evaluacion/reevaluaciones',
    permission: 'evaluations.conduct_oral_exam',
  },
  myCampaigns: {
    // Worker-facing campaign card: the exam endpoints accept either this
    // worker permission or the administrative one (backend OR tuple), so
    // RR.HH./Gerencia still take trainings without losing campaign admin.
    path: '/evaluacion/mis-campanas',
    permission: 'evaluations.my_campaigns',
    orPermissions: ['evaluations.view_campaign'],
  },
  employees: {
    path: '/trabajadores',
    permission: 'employees.view_employee',
  },
  idBadgePrint: {
    path: '/trabajadores/fotochecks',
    permission: 'employees.print_id_badge',
  },
  // Ungated on purpose: the company's manual library is for everyone. The
  // backend permission lands later, and the gate goes into this entry then.
  myManuals: { path: '/manuales' },
  myProfile: { path: '/mi-ficha', permission: 'employees.view_own_profile' },
  // Menu maintainer. Reading the tree is enough to open it; editing buttons
  // check `navigation.manage_menu_item` on their own.
  menuItems: { path: '/configuracion/menu', permission: 'navigation.view_menu_item' },
  roles: { path: '/roles', permission: 'iam.view_role' },
  permissions: { path: '/permissions', permission: 'iam.view_role' },
  hrHome: { path: '/rrhh', anyRoles: ['HR'] },
  managerHome: { path: '/jefe-area', anyRoles: ['MANAGER'] },
  employeeHome: { path: '/inicio', anyRoles: ['EMPLOYEE'] },
  manualReader: {
    path: '/inicio/capacitaciones/:id/manuales/:versionId',
    permission: 'evaluations.read_campaign_manual',
  },
  manualDetail: { path: '/organizacion/manuales/:id', permission: 'organization.view_department' },
  staffingRequests: {
    path: '/convocatorias/solicitudes',
    permission: 'staffing.view_staffing_request',
  },
  news: { path: '/novedades', permission: 'news.view_news' },
  newsAdmin: {
    path: '/novedades/gestion',
    permission: 'news.manage_news',
  },
} satisfies Record<string, ViewDef>;

export type ViewKey = keyof typeof VIEWS;

export const roleHomes: Partial<Record<string, ViewKey>> = {
  HR: 'hrHome',
  MANAGER: 'managerHome',
  EMPLOYEE: 'employeeHome',
  EXECUTIVE: 'dashboard',
  SUPPORT_TI: 'dashboard',
};

/** UI convenience only: APIs remain the authority for every operation. */
export function canSeeView(user: CurrentUser | null, view: ViewDef): boolean {
  if (!user) return false;
  if (user.scope.is_global) return true;
  const candidates = [view.permission, ...(view.orPermissions ?? [])].filter(
    (codename): codename is string => Boolean(codename),
  );
  if (candidates.length && !candidates.some((codename) => user.permissions.includes(codename))) {
    return false;
  }
  if (view.anyRoles && !view.anyRoles.some((role) => user.role_codes.includes(role))) return false;
  return true;
}
